import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';

/**
 * MobileAppBanner — full-screen interstitial for mobile web visitors.
 *
 * The web app doesn't render well on mobile browsers (the main component
 * is a 45K-line desktop-first monolith). Rather than maintain responsive
 * CSS for every view, we redirect mobile visitors to the native app
 * (or app store) and keep web as the desktop experience.
 *
 * Rendering rules:
 *   - Show only when: viewport ≤ 768px AND touch input AND not previously dismissed
 *   - NEVER show on desktop (viewport guard + touch guard = belt + suspenders)
 *   - NEVER show when `?noredirect=1` is in the URL (for Apple reviewer, devs)
 *   - Dismissal persists in localStorage with 7-day TTL
 *
 * If Universal Links / App Links are configured (see
 * web/public/.well-known/apple-app-site-association + assetlinks.json),
 * clicking "Get the App" on a device with the app installed will open
 * the app directly. Otherwise falls through to the App Store / Play Store.
 *
 * Event tracking (best-effort — silently no-ops if analytics not consented):
 *   - mobile_banner_shown
 *   - mobile_banner_clicked
 *   - mobile_banner_dismissed
 */

const DISMISS_KEY = 'arrivo_app_banner_dismissed';
const DISMISS_TTL_DAYS = 7;

const APP_STORE_URL = 'https://apps.apple.com/app/id6760428725'; // Arrivo — ascAppId from mobile/eas.json
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.wanderaway.wander';
const UNIVERSAL_LINK_BASE = 'https://app.arrivotravel.ai';

function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
}

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const { dismissedAt } = JSON.parse(raw);
    if (!dismissedAt) return false;
    const ttlMs = DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() - dismissedAt < ttlMs;
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ dismissedAt: Date.now() }));
  } catch {
    // ignore — private mode / quota
  }
}

function hasNoRedirectQuery() {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).has('noredirect');
  } catch {
    return false;
  }
}

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

function track(eventName, properties = {}) {
  // Best-effort PostHog tracking. Silently no-ops if PostHog is not
  // initialized or the user hasn't consented. We don't block on this.
  try {
    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture(eventName, properties);
    }
  } catch {
    // analytics not configured or not consented — ignore
  }
}

const MobileAppBanner = () => {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState('other');

  useEffect(() => {
    if (hasNoRedirectQuery()) return;
    if (isDismissed()) return;
    if (!isMobileViewport()) return;

    setPlatform(detectPlatform());
    setShow(true);
    track('mobile_banner_shown', {
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      platform: detectPlatform(),
      path: window.location.pathname,
    });
  }, []);

  const handleOpenApp = () => {
    track('mobile_banner_clicked', {
      platform,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    });
    // Try the universal link first — on iOS/Android with the app installed,
    // the OS will intercept and open the app. Otherwise the link takes them
    // to the web URL, which then bounces to the app store.
    // We detect the app-install-failure fallback after 1s.
    const universalUrl = `${UNIVERSAL_LINK_BASE}${window.location.pathname}${window.location.search}`;
    const storeUrl = platform === 'ios' ? APP_STORE_URL :
                     platform === 'android' ? PLAY_STORE_URL :
                     APP_STORE_URL;
    // Try universal link (if app installed, this opens the app)
    window.location.href = universalUrl;
    // If the app is NOT installed, the universal link falls through to web.
    // After 1.5s, if we're still here, push to store.
    const fallbackTimer = setTimeout(() => {
      window.location.href = storeUrl;
    }, 1500);
    // If page unloads (app took over), cancel fallback
    window.addEventListener('pagehide', () => clearTimeout(fallbackTimer), { once: true });
  };

  const handleDismiss = () => {
    setDismissed();
    track('mobile_banner_dismissed', {
      platform,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    });
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#F5F5F0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        textAlign: 'center',
      }}
    >
      {/* Logo */}
      <img
        src="/arrivo-icon.png"
        alt="Arrivo"
        style={{ width: '96px', height: '96px', marginBottom: '24px' }}
      />

      {/* Headline */}
      <h1
        style={{
          fontFamily: "'Gulfs Display', 'Batangas', sans-serif",
          fontSize: '32px',
          fontWeight: 800,
          color: '#313143',
          letterSpacing: '0.05em',
          marginBottom: '8px',
        }}
      >
        arrivo
      </h1>
      <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '32px', fontWeight: 500 }}>
        plan less. explore more.
      </p>

      <h2
        style={{
          fontSize: '22px',
          fontWeight: 700,
          color: '#1e293b',
          marginBottom: '12px',
          maxWidth: '320px',
          lineHeight: 1.3,
        }}
      >
        Arrivo is built for mobile
      </h2>
      <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '320px', lineHeight: 1.5, marginBottom: '32px' }}>
        Get the full experience in our native app — maps, offline access, and trip-day live updates.
      </p>

      {/* Primary CTA */}
      <button
        type="button"
        onClick={handleOpenApp}
        style={{
          background: '#0d9488',
          color: 'white',
          border: 'none',
          borderRadius: '14px',
          padding: '14px 32px',
          fontSize: '16px',
          fontWeight: 600,
          width: '100%',
          maxWidth: '320px',
          marginBottom: '12px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(13,148,136,0.25)',
        }}
      >
        Get the App
      </button>

      {/* Secondary CTA */}
      <button
        type="button"
        onClick={handleDismiss}
        style={{
          background: 'transparent',
          color: '#64748b',
          border: 'none',
          padding: '12px 32px',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        Continue on web
      </button>
    </div>
  );
};

export default MobileAppBanner;
